class BankStatementParser {
  statements: BankStatement[];
  verifyLastSix: boolean;
  required_days: number;

  constructor(statements: BankStatement[], required_days: number) {
    this.statements = statements.filter(
      i => i.statement_end_date && i.statement_end_date,
    );
    this.verifyLastSix = true;
    this.required_days = required_days;
  }

  lastMonthEndDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 15);
    return date;
  };

  getGapBetweenDates = (date: Date, lastDate: Date) => {
    const differenceInMilliseconds = date.getTime() - lastDate.getTime();
    const millisecondsInADay = 1000 * 60 * 60 * 24;
    const differenceInDays = Math.floor(
      differenceInMilliseconds / millisecondsInADay,
    );
    return Math.abs(differenceInDays);
  };

  banksStatements = () => {
    let dates = {} as Record<string, string>;
    this.statements.forEach(statement => {
      const start = statement.statement_start_date;
      const end = statement.statement_end_date;
      const range = this.getDates(new Date(start!), new Date(end!));
      dates = {...dates, ...range};
    });
    return dates;
  };

  lastSixMonthFromDate = () => {
    const date = new Date();
    date.setDate(new Date().getDate() - this.required_days);
    return date;
  };

  lastSixMonthRange = () => {
    const endDate = this.lastMonthEndDate();
    const startDate = this.lastSixMonthFromDate();
    return {startDate, endDate};
  };

  formatDate = (date: Date) => {
    const addZero = (num: number) => (num < 10 ? `0${num}` : num);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${year}-${addZero(month)}-${addZero(day)}`;
  };

  getDates = (startDate: Date, endDate: Date) => {
    const dates = {} as Record<string, string>;

    const theDate = new Date(startDate);
    while (theDate <= endDate) {
      const formatDate = this.formatDate(new Date(theDate));
      dates[formatDate] = formatDate;
      theDate.setDate(theDate.getDate() + 1);
    }
    return dates;
  };

  getNotAvailable = () => {
    const stateMentDates = this.banksStatements();
    const last6MonthRange = this.lastSixMonthRange();
    const dates = this.getDates(
      last6MonthRange.startDate,
      last6MonthRange.endDate,
    );
    const notAvailableMonths = {} as Record<string, number>;
    Object.keys(dates).forEach(date => {
      if (!stateMentDates[date]) {
        const month = new Date(date).toLocaleString('default', {
          month: 'short',
        });
        const year = new Date(date).getFullYear();
        const monthWithYear = `${year} ${month}`;
        notAvailableMonths[monthWithYear] = notAvailableMonths[monthWithYear]
          ? notAvailableMonths[monthWithYear] + 1
          : 1;
      }
    });

    return notAvailableMonths;
  };

  lastSixMonths = () => {
    const last6MonthRange = this.lastSixMonthRange();
    const dates = this.getDates(
      last6MonthRange.startDate,
      last6MonthRange.endDate,
    );
    const months = {} as Record<string, string>;
    Object.keys(dates).forEach(date => {
      const month = new Date(date).toLocaleString('default', {
        month: 'short',
      });
      const year = new Date(date).getFullYear();
      const monthWithYear = `${year} ${month}`;
      months[monthWithYear] = monthWithYear;
    });
    return months;
  };

  checkIfNotNull = () => {
    return this.statements.some(
      statement =>
        statement.statement_start_date && statement.statement_end_date,
    );
  };

  get valid_months() {
    const months = [] as {
      label: string;
      remaining: number;
      disabled?: boolean;
    }[];
    if (!this.checkIfNotNull() || !this.verifyLastSix) {
      return months;
    }
    const sixMonths = this.lastSixMonths();
    const notAvailable = this.getNotAvailable();

    Object.keys(sixMonths).forEach(month => {
      if (notAvailable[month]) {
        months.push({
          label: month.split(' ').reverse().join(' '),
          remaining: notAvailable[month],
          disabled: true,
        });
      } else {
        months.push({label: month, remaining: 0});
      }
    });

    const currentDate = new Date();
    const endDate = this.lastMonthEndDate();
    const currentMonth = new Date(currentDate).toLocaleString('default', {
      month: 'short',
    });

    const endMonth = new Date(endDate).toLocaleString('default', {
      month: 'short',
    });
    if (currentMonth.toString() === endMonth.toString()) {
      return months.slice(-7);
    } else {
      return months.slice(-6);
    }
  }

  get is_valid() {
    const validMonths = this.valid_months;
    if (validMonths.length === 0 && this.statements.length > 0) {
      return true;
    } else if (validMonths.length === 0 && this.statements.length === 0) {
      return false;
    } else {
      return validMonths.every(month => !month.disabled);
    }
  }

  get name_not_matched() {
    return this.statements.some(
      statement => "name_matched" in statement && !statement.name_matched
    );
  }

  get remaining_days() {
    const validMonths = this.valid_months;
    const remaining = validMonths.reduce(
      (acc, month) => acc + month.remaining,
      0,
    );
    return Math.min(remaining, this.required_days);
  }
}

export default BankStatementParser;
